import { Injectable, inject, computed, resource } from "@angular/core";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import type { IiFirmUserRolesWithUsersMap, IiFirmRoleUsersMap, IiFirmRoleWithUsers, IiFirmRoleRaw, IiFirmRole } from "@interfaces/index";
import { IFirmUserRolesService } from "@shared/ifirm-user-roles-service/ifirm-user-roles.service";
import { UsersService } from "@shared/users-service/users.service";
import { TranslateService } from "@ngx-translate/core";

export const MANAGER_ROLE_ID = "1";
export const PARTNER_ROLE_ID = "2";
export const PREPARER_ROLE_ID = "3";
export const REVIEWER_ROLE_ID = "4";
export const ADMINISTRATION_ROLE_ID = "9";

@Injectable({ providedIn: "root" })
export class IFirmRolesService {
    private readonly translateService = inject(TranslateService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly iFirmUserRolesService = inject(IFirmUserRolesService);
    private readonly usersService = inject(UsersService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private iFirmRolesLoadPromise: Promise<IiFirmRole[]> | undefined;
    iFirmRoles: IiFirmRole[] = [];

    private readonly iFirmRolesResource = resource<IiFirmRole[], undefined>({
        defaultValue: [],
        loader: () => this.getiFirmRoles(),
    });
    public sIFirmRoles = computed<IiFirmRole[]>(() => this.iFirmRolesResource.value());

    readonly rolesWithUsers = computed<IiFirmUserRolesWithUsersMap>(() => {
        const roles = this.sIFirmRoles();
        const usersByUserId = this.usersService.usersById()?.fromUserId;
        const userRolesByRoleId = this.iFirmUserRolesService.roleUserIds();
        const rolesWithUsersByRoleId: Map<string, IiFirmRoleWithUsers> = new Map();
        if (!roles?.length || !usersByUserId || !userRolesByRoleId?.size) return rolesWithUsersByRoleId;

        for (const role of roles) {
            const userRoles = userRolesByRoleId.get(role.Id) ?? [];

            const roleUsersMap: IiFirmRoleUsersMap = new Map();
            for (const userRole of userRoles) {
                if (roleUsersMap.has(userRole.UserId)) continue;
                const user: any = usersByUserId.get(userRole.UserId);
                if (user) roleUsersMap.set(user.Id, user);
            }

            for (const base of usersByUserId.values()) {
                if (roleUsersMap.has(base.Id) || roleUsersMap.has(base.IFirmUserId)) continue;
                const user: any = { ...base };
                user.Active = false;
                if (base.Active) {
                    user.FullName =
                        this.translateService.instant("General.RoleNoAccess") + (user.completeName?.fullName ?? `${user.FirstName} ${user.LastName}`);
                    user.ReverseFullName = this.translateService.instant("General.RoleNoAccess") + `${user.LastName}, ${user.FirstName}`;
                }
                roleUsersMap.set(user.Id, user);
            }

            rolesWithUsersByRoleId.set(role.Id, { ...role, Users: roleUsersMap });
        }

        return rolesWithUsersByRoleId;
    });

    async loadiFirmRoles(): Promise<IiFirmRole[]> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getiFirmRoles", {}, "gotiFirmRoles");
            if (result.status === "success") {
                this.iFirmRoles = this.translateiFirmRoles(result.roles as IiFirmRoleRaw[]);
                return this.iFirmRoles;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        }
    }

    async getiFirmRoles(): Promise<IiFirmRole[]> {
        if (!this.iFirmRolesLoadPromise) {
            this.iFirmRolesLoadPromise = this.loadiFirmRoles();
        }
        return this.iFirmRolesLoadPromise;
    }

    translateiFirmRoles(iFirmRoles: IiFirmRoleRaw[]): IiFirmRole[] {
        return iFirmRoles.map((iFirmRole) => {
            return {
                Id: `${iFirmRole.Id}`,
                Description: {
                    "en-CA": iFirmRole.RoleName,
                    "fr-CA": iFirmRole.RoleNameLanguage1,
                },
                Default: iFirmRole.IsDefault,
                IsPartner: iFirmRole.IsPartner,
                IsManager: iFirmRole.IsManager,
            };
        });
    }

    getUserNameById(roleId: string, userId: string): string {
        if (userId == null || userId == undefined) return "";
        const role = this.getRoleById(roleId);
        const users = role ? role.Users : null;
        const currentUserId = this.usersService.getUserById(userId)?.Id ?? userId;
        const user = users?.get(currentUserId);
        if (user) {
            let name = user.ReverseFullName ?? user.FullName ?? "";
            if (!name) {
                name = user.LastName ?? "";
                if (name.length > 0) name += ", ";
                name += user.FirstName;
            }
            return name;
        }
        return "";
    }

    getRoleById(roleId: string, removeInactive = false): IiFirmRoleWithUsers | undefined {
        const roles = this.rolesWithUsers();
        const roleWithUsers = roles?.get(roleId);
        if (!roleWithUsers) return;
        if (!removeInactive) return roleWithUsers;

        return { ...roleWithUsers, Users: this.getActiveUsersFromRole(roleWithUsers) };
    }

    getPreparer(removeInactive = false): IiFirmRoleWithUsers | undefined {
        return this.getDefaultRoleByDescriptionEnglish("Preparer", removeInactive);
    }

    getReviewer(removeInactive = false): IiFirmRoleWithUsers | undefined {
        return this.getDefaultRoleByDescriptionEnglish("Reviewer", removeInactive);
    }

    getPartner(removeInactive = false): IiFirmRoleWithUsers | undefined {
        return this.getDefaultRoleByDescriptionEnglish("Partner", removeInactive);
    }

    getManager(removeInactive = false): IiFirmRoleWithUsers | undefined {
        return this.getDefaultRoleByDescriptionEnglish("Manager", removeInactive);
    }

    getActiveUsersFromRole(role: IiFirmRoleWithUsers): IiFirmRoleUsersMap {
        return new Map(Array.from(role.Users.entries()).filter(([, user]) => user.Active));
    }

    getDefaultRoleByDescriptionEnglish(roleName: string, removeInactive = false): IiFirmRoleWithUsers | undefined {
        const rolesWithUsers = this.rolesWithUsers();
        if (!rolesWithUsers?.size) return;

        for (const role of rolesWithUsers.values()) {
            if (role.Default && role.Description["en-CA"] === roleName) {
                if (!removeInactive) return role;
                return { ...role, Users: this.getActiveUsersFromRole(role) };
            }
        }
    }
}
